import { Component, EventEmitter, Inject, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { UnsavedChangesComponent } from '../unsaved-changes/unsaved-changes.component';
import { CaseCommunicationService } from '@core/services/case-communication.service';
import { ErrorService, LayoutService } from '@core/services';
import { indicate, noChangesReplacer, closeDialogOnEscape } from '@shared/helpers';
import { takeUntil } from 'rxjs/operators';
import { StiiraError } from '@core/models';
import { AssignEmployersMode } from '@core/enums/assign-employers-mode.enum';
import { ActionItemsTemplateDetails, AssignedEmployer } from '@core/models/action-item-templates/action-item-template-details.model';
import { ActionItemsTemplatesService } from '@core/services/action-item-templates.service';
import { AssignedEmployerDialogForm, AssignedEmployerOption, EmployerFormGroup } from '@core/models/shared/assigned-employers-dialog.model';
import { TemplateDetails } from '@core/models/case-communication/template-details.model';
import { ActionItemDetails } from '@core/models/action-item-templates/action-item-details.model';
import { position, onDragEnded } from '@shared/helpers/dialog-drag-constraints';
import { LeaveReasonsService } from '@core/services/leave-reasons.service';
import { LeaveReasonDetails } from '@core/models/leave-reasons/leave-reason-details.model';

@Component({
    selector: 'app-edit-assigned-employers',
    templateUrl: './edit-assigned-employers.component.html',
    styleUrls: ['./edit-assigned-employers.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EditAssignedEmployersComponent implements OnInit {
  static readonly componentName = 'EditAssignedEmployersComponent';
  
  @Output() isEditing = new EventEmitter<boolean>();
  
  public form: FormGroup<AssignedEmployerDialogForm>;
  public formInitValues: any;
  public isSubmitting$: Subject<boolean>;
  public isMobile$: Observable<boolean>;
  public position = position;
  public onDragEnded = onDragEnded;

  private formChangeEmitted: boolean = false;
  private destroy$: Subject<void> = new Subject<void>();

  get noChanges(): boolean {
    return JSON.stringify(this.form.value, noChangesReplacer) === JSON.stringify(this.formInitValues, noChangesReplacer);
  }

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: { 
      mode: AssignEmployersMode;
      id: number;
      employers: AssignedEmployer[];
      employerOptions: AssignedEmployerOption[];
      showDefaults?: boolean;
      sysText: any; 
      unsavedChangesSysText: any
    },
    private dialogRef: MatDialogRef<EditAssignedEmployersComponent>,
    private dialog: MatDialog,
    private fb: FormBuilder,
    private errorService: ErrorService,
    private caseCommunicationService: CaseCommunicationService,
    private actionItemsTemplatesService: ActionItemsTemplatesService,
    private leaveReasonsService: LeaveReasonsService,
    private layoutService: LayoutService
    ) { 
    this.isSubmitting$ = new Subject();
    this.form = this.fb.group({
      employersArray: this.fb.array<FormGroup<EmployerFormGroup>>([])
    })
  }

  get employers(): FormArray<FormGroup<EmployerFormGroup>> {
    return this.form.controls.employersArray as FormArray<FormGroup<EmployerFormGroup>>;
  }

  ngOnInit(): void {
    this.isMobile$ = this.layoutService.isMobile$();
    closeDialogOnEscape(this.dialogRef, this.isSubmitting$, () => this.cancel());

    this.setupForm();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public setFormErrors() {
    setTimeout(() => {
      this.form.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          if (!this.formChangeEmitted && !this.noChanges) {
            this.isEditing.emit(true);
            this.formChangeEmitted = true;
          } else if (this.noChanges) {
            this.isEditing.emit(false);
            this.formChangeEmitted = false;
          }
        });
    }, 0);
  }

  public save(): void {
    let postObservable$: Observable<TemplateDetails | ActionItemsTemplateDetails | ActionItemDetails | LeaveReasonDetails>;

    switch (this.data.mode) {
      case AssignEmployersMode.EmailTemplate: {
        postObservable$ = this.caseCommunicationService.postTemplateAssignedEmployers({ 
          templateId: this.data.id,
          employers: this.employers.value.filter(v => v.isAssigned).map(v => v.employerId)
        });
        break;
      }
      case AssignEmployersMode.ActionItemsTemplate: {
        postObservable$ = this.actionItemsTemplatesService.postActionItemsTemplateAssignedEmployers({ 
          templateId: this.data.id,
          assignedEmployers: this.employers.value.filter(v => v.isAssigned).map(v => {
            return {
              employerId: v.employerId,
              isDefault: v.isDefault
            }
          })
        });
        break;
      }
      case AssignEmployersMode.ActionItem: {
        postObservable$ = this.actionItemsTemplatesService.postActionItemAssignedEmployers({ 
          actionItemId: this.data.id,
          assignedEmployers: this.employers.value.filter(v => v.isAssigned).map(v => v.employerId)
        });
        break;
      }
      case AssignEmployersMode.LeaveReason: {
        postObservable$ = this.leaveReasonsService.postLeaveReasonAssignedEmployers({ 
          leaveReasonId: this.data.id,
          assignedEmployers: this.employers.value.filter(v => v.isAssigned).map(v => v.employerId)
        });
        break;
      }
    }

    postObservable$
      .pipe(indicate(this.isSubmitting$))
      .subscribe((res) => {
        this.dialogRef.close(res);
      }, (err: StiiraError) => this.errorService.setFormModelStateErrors(this.form, err.modelStateErrors));
  }

  public cancel(): void {
    if (this.noChanges) {
      this.dialogRef.close();
    } else {
      this.openUnsavedChangesDialog();
    }
  }

  public openUnsavedChangesDialog(): void {
    const dialogConfig: MatDialogConfig = {
      id: UnsavedChangesComponent.componentName,
      width: '300px',
      data: this.data.unsavedChangesSysText
    };
    
    this.dialog.open(UnsavedChangesComponent, dialogConfig)
      .afterClosed().subscribe((res: boolean) => {
        if (res) {
          this.dialogRef.close();
        }
      });
  }

  private setupForm(): void {
    this.data.employerOptions.forEach((eo) => {
      const selectedEmployer = this.data.employers.find(e => e.id === eo.id);
      const formGroup = this.fb.group<EmployerFormGroup>({
        employerId: this.fb.control(eo.id as number),
        employerName: this.fb.control(eo.description),
        isAssigned: this.fb.control(selectedEmployer ? true : false),
        isDefault: this.fb.control(selectedEmployer?.isDefault ?? false),
        defaultExists: this.fb.control(eo.defaultExists)
      });

      if (!selectedEmployer) {
        formGroup.controls.isDefault.disable();
      }

      formGroup.controls.isAssigned.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((res) => {
          if (res) {
            formGroup.controls.isDefault.enable();
          } else {
            formGroup.controls.isDefault.setValue(false);
            formGroup.controls.isDefault.disable();
          }
        });

      this.employers.push(formGroup);
    });

    this.formInitValues = { ...this.form.value };
    this.setFormErrors();
  }
}
